import {
  Directive,
  Input,
  OnChanges,
  SimpleChanges,
  ElementRef,
  Renderer2,
  OnDestroy,
  AfterViewInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { MODULE_LOADING_MESSAGES } from '../../services/module.config';

@Directive({
  selector: '[tableLoading]',
})
export class TableLoadingDirective
  implements OnChanges, OnDestroy, AfterViewInit
{
  @Input('tableLoading') isLoading: boolean = false;
  @Input() skeletonRows: number = 5;
  @Input() moduleName: string = 'generic';

  private skeletonElements: HTMLTableRowElement[] = [];
  private badgeElement: HTMLDivElement | null = null;
  private messageInterval: any = null;
  private currentMessageIndex = 0;
  private isInitialized = false;
  private activeMessages: string[] = [];

  constructor(
    private el: ElementRef,
    private renderer: Renderer2,
    private router: Router
  ) {}

  ngAfterViewInit() {
    this.isInitialized = true;
    this.updateState();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isLoading'] && this.isInitialized) {
      this.updateState();
    }
  }

  ngOnDestroy() {
    this.cleanupMessageRotation();
    this.removeSkeletonRows();
    this.removeBadge();
  }

  private getMessagesForModule(module: string): string[] {
    try {
      const url = this.router.url; // e.g. "/main/leads" or "/main/leads?someParam=1"
      const pathWithoutParams = url.split('?')[0]; // "/main/leads"
      const segments = pathWithoutParams.split('/'); // ["", "main", "leads"]
      const lastSegment = segments[segments.length - 1]; // "leads"
      
      if (lastSegment && MODULE_LOADING_MESSAGES[lastSegment]) {
        return MODULE_LOADING_MESSAGES[lastSegment];
      }
      if (lastSegment === 'account' || lastSegment === 'company') {
        return MODULE_LOADING_MESSAGES['accounts'];
      }
    } catch (e) {
      console.warn('Router URL resolution failed, using fallback', e);
    }

    const key = String(module || '').toLowerCase().trim();
    if (MODULE_LOADING_MESSAGES[key]) {
      return MODULE_LOADING_MESSAGES[key];
    }
    
    // Mapping keys that might not match exact segment but have input parameter
    if (key === 'lead') return MODULE_LOADING_MESSAGES['leads'];
    if (key === 'deal') return MODULE_LOADING_MESSAGES['deals'];
    if (key === 'contact') return MODULE_LOADING_MESSAGES['contacts'];
    if (key === 'account' || key === 'accounts' || key === 'company') {
      return MODULE_LOADING_MESSAGES['accounts'];
    }
    if (key === 'invoice' || key === 'billing') {
      return MODULE_LOADING_MESSAGES['invoices'];
    }
    if (key === 'analytics') return MODULE_LOADING_MESSAGES['forecast'];
    if (key === 'quote') return MODULE_LOADING_MESSAGES['quotes'];
    if (key === 'product' || key === 'products') {
      return MODULE_LOADING_MESSAGES['product-configuration'];
    }
    if (key === 'project' || key === 'projects') {
      return MODULE_LOADING_MESSAGES['manage-projects'];
    }
    if (key === 'support' || key === 'ticket' || key === 'tickets') {
      return MODULE_LOADING_MESSAGES['manage-tickets'];
    }
    if (key === 'user' || key === 'users' || key === 'audit') {
      return MODULE_LOADING_MESSAGES['user-logs'];
    }
    if (
      key === 'settings' ||
      key === 'configuration' ||
      key === 'template' ||
      key === 'whatsapp'
    ) {
      return MODULE_LOADING_MESSAGES['user-settings'];
    }

    return MODULE_LOADING_MESSAGES['generic'];
  }

  private updateState() {
    if (this.isLoading) {
      this.showSkeleton();
      this.showBadge();
    } else {
      this.hideSkeleton();
      this.hideBadge();
    }
  }

  private showSkeleton() {
    const table = this.el.nativeElement;
    this.renderer.addClass(table, 'table-loading-active');

    const parent = table.parentElement;
    if (parent) {
      this.renderer.setStyle(parent, 'position', 'relative');
    }

    this.removeSkeletonRows();

    const headerCells = table.querySelectorAll(
      'th, .mat-mdc-header-cell, .mat-header-cell',
    );
    const columnCount = headerCells.length || 5;

    const tbody = table.querySelector('tbody') || table;
    const widths = ['45%', '75%', '60%', '80%', '50%', '70%', '40%', '85%'];

    for (let i = 0; i < this.skeletonRows; i++) {
      const tr = this.renderer.createElement('tr');
      this.renderer.addClass(tr, 'skeleton-row');
      this.renderer.addClass(tr, 'mat-row');
      this.renderer.addClass(tr, 'mdc-data-table__row');

      for (let j = 0; j < columnCount; j++) {
        const td = this.renderer.createElement('td');
        this.renderer.addClass(td, 'skeleton-cell');
        this.renderer.addClass(td, 'mat-cell');
        this.renderer.addClass(td, 'mdc-data-table__cell');

        const bar = this.renderer.createElement('div');
        this.renderer.addClass(bar, 'skeleton-bar');

        const widthVal = widths[(i + j) % widths.length];
        this.renderer.setStyle(bar, 'width', widthVal);

        this.renderer.appendChild(td, bar);
        this.renderer.appendChild(tr, td);
      }

      this.renderer.appendChild(tbody, tr);
      this.skeletonElements.push(tr);
    }
  }

  private hideSkeleton() {
    const table = this.el.nativeElement;
    this.renderer.removeClass(table, 'table-loading-active');
    this.removeSkeletonRows();
  }

  private removeSkeletonRows() {
    this.skeletonElements.forEach((row) => {
      if (row.parentNode) {
        this.renderer.removeChild(row.parentNode, row);
      }
    });
    this.skeletonElements = [];
  }

  private showBadge() {
    if (this.badgeElement) return;

    const table = this.el.nativeElement;
    const parent = table.parentElement;
    if (!parent) return;

    this.badgeElement = this.renderer.createElement('div');
    this.renderer.addClass(this.badgeElement, 'table-loading-badge');

    const spinner = this.renderer.createElement('div');
    this.renderer.addClass(spinner, 'badge-spinner');
    this.renderer.appendChild(this.badgeElement, spinner);

    const textSpan = this.renderer.createElement('span');
    this.renderer.addClass(textSpan, 'badge-text');
    this.activeMessages = this.getMessagesForModule(this.moduleName);
    this.currentMessageIndex = 0;
    this.renderer.setProperty(
      textSpan,
      'innerText',
      this.activeMessages[this.currentMessageIndex],
    );
    this.renderer.appendChild(this.badgeElement, textSpan);

    this.renderer.appendChild(parent, this.badgeElement);

    setTimeout(() => {
      if (this.badgeElement) {
        this.renderer.addClass(this.badgeElement, 'show');
      }
    }, 50);

    this.setupMessageRotation(textSpan);
  }

  private hideBadge() {
    if (!this.badgeElement) return;

    const badge = this.badgeElement;
    this.renderer.removeClass(badge, 'show');
    this.cleanupMessageRotation();

    setTimeout(() => {
      if (badge.parentNode) {
        this.renderer.removeChild(badge.parentNode, badge);
      }
      if (this.badgeElement === badge) {
        this.badgeElement = null;
      }
    }, 300);
  }

  private removeBadge() {
    if (this.badgeElement && this.badgeElement.parentNode) {
      this.renderer.removeChild(
        this.badgeElement.parentNode,
        this.badgeElement,
      );
    }
    this.badgeElement = null;
  }

  private setupMessageRotation(textSpan: HTMLElement) {
    this.cleanupMessageRotation();

    this.messageInterval = setInterval(() => {
      if (!this.badgeElement) return;

      this.renderer.addClass(textSpan, 'fade-out');

      setTimeout(() => {
        if (!this.badgeElement) return;

        this.currentMessageIndex =
          (this.currentMessageIndex + 1) % this.activeMessages.length;
        this.renderer.setProperty(
          textSpan,
          'innerText',
          this.activeMessages[this.currentMessageIndex],
        );

        this.renderer.removeClass(textSpan, 'fade-out');
        this.renderer.addClass(textSpan, 'fade-in');

        setTimeout(() => {
          this.renderer.removeClass(textSpan, 'fade-in');
        }, 200);
      }, 200);
    }, 2000);
  }

  private cleanupMessageRotation() {
    if (this.messageInterval) {
      clearInterval(this.messageInterval);
      this.messageInterval = null;
    }
  }
}
